import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShipmentItem } from '../api/types';
import { formatKopeks } from '../lib/money';
import { formatQuantityMilli } from '../lib/quantity';

/** Line sum, kopeks — mirrors backend shipment-math.lineSum (round identically). */
function lineSum(quantityMilli: number, priceKopeks: number): number {
  return Math.round((quantityMilli * priceKopeks) / 1000);
}

/**
 * Read-only line items of a накладная. `priceMode` picks which price to show:
 * 'sell' (store side — «Моя цена/сумма», issue price) or 'buy' (sklad side —
 * «Цена/сумма закупки»). The Сумма total reconciles with the paired operation
 * amount (sell → issue amount, buy → purchase amount).
 */
export function ShipmentItemsTable({
  items,
  priceMode,
}: {
  items: ShipmentItem[];
  priceMode: 'buy' | 'sell';
}): JSX.Element {
  const { t } = useTranslation();

  const unitPrice = (item: ShipmentItem): number | null =>
    priceMode === 'sell' ? item.issuePriceKopeks : item.buyPriceKopeks;
  const rowSum = (item: ShipmentItem): number | null => {
    if (priceMode === 'sell') {
      return item.issuePriceKopeks == null ? null : lineSum(item.quantityMilli, item.issuePriceKopeks);
    }
    return item.buySumKopeks;
  };
  const total = items.reduce((sum, item) => sum + (rowSum(item) ?? 0), 0);

  const columns: ColumnsType<ShipmentItem> = [
    {
      title: '№',
      key: 'idx',
      width: 44,
      align: 'right',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      title: t('shipments.colItem'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Typography.Text strong>{name}</Typography.Text>,
    },
    {
      title: t('shipments.colQty'),
      dataIndex: 'quantityMilli',
      key: 'qty',
      align: 'right',
      render: (qty: number) => formatQuantityMilli(qty),
    },
    {
      title: t(priceMode === 'sell' ? 'shipments.colMyPrice' : 'shipments.buyPriceLabel'),
      key: 'price',
      align: 'right',
      render: (_v, item) => {
        const p = unitPrice(item);
        return p == null ? '—' : formatKopeks(p);
      },
    },
    {
      title: t(priceMode === 'sell' ? 'shipments.colMySum' : 'shipments.buyTotal'),
      key: 'sum',
      align: 'right',
      render: (_v, item) => {
        const s = rowSum(item);
        return s == null ? '—' : <Typography.Text strong>{formatKopeks(s)}</Typography.Text>;
      },
    },
  ];

  return (
    <Table<ShipmentItem>
      dataSource={items}
      columns={columns}
      rowKey="id"
      size="small"
      pagination={false}
      scroll={{ x: 420 }}
      summary={() => (
        <Table.Summary.Row style={{ fontWeight: 600 }}>
          <Table.Summary.Cell index={0}>{''}</Table.Summary.Cell>
          <Table.Summary.Cell index={1}>{t('shipments.totalRow')}</Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            {''}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            {''}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            {formatKopeks(total)}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
}
